var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, styled, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { generatePath } from "react-router";
import { formatCurrency } from "../../../../../utils";
import { getProductTitle } from "../../../../utils/orders";
import { ProductPreview } from "../../../../Components/ShoppingCartContent";
import { getPackageItemDescription, groupPackageItemsByProduct, OrderLineShortDescription, SubProducts, } from "../../../../Components/Checkout/OrderContent";
import Button from "@/react/components/Common/Button";
import useMobileRoutes from "@/react/Webshop/Mobile/hooks/useMobileRoutes";
import useIsMobileDevice from "@/react/hooks/useIsMobileDevice";
const Line = styled("div")(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 0),
}));
const PreviewColumn = styled("div")(({ theme }) => ({
    flex: "0 0 30%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(1),
    backgroundColor: theme.palette.primary.light,
}));
const DetailsColumn = styled("div")({
    flex: "1 1 auto",
    minWidth: 0,
});
const PriceRow = styled("div")({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
});
const QuantityBox = styled("div")(({ theme }) => ({
    minWidth: 28,
    padding: theme.spacing(0.25, 0.75),
    border: `1px solid ${theme.palette.text.primary}`,
    textAlign: "center",
}));
const PackageProductList = styled("ul")(({ theme }) => ({
    listStyle: "none",
    margin: theme.spacing(1, 0),
    padding: 0,
    "& > li > ul": {
        listStyle: "disc",
        margin: theme.spacing(0, 0, 1, 0),
        paddingLeft: theme.spacing(2),
    },
}));
const BuyAgainButton = ({ orderLine, buyAgain, onBuyAgain, }) => {
    var _a, _b;
    const [inProgress, setInProgress] = useState(false);
    const routes = useMobileRoutes();
    // Full width on phones; on wider screens the button sizes to its label.
    const fullWidth = useIsMobileDevice();
    // PHP sends template: null when the template instance can't be loaded with
    // the order's price list, which leaves nothing for the cart API to add. The
    // buy-again link copies the order part on the server instead.
    const templateIsMissing = "template" in orderLine && !orderLine.template;
    const packageIsIncomplete = "package" in orderLine && !orderLine.package.canRebuildFromHistory;
    if (!onBuyAgain || templateIsMissing || packageIsIncomplete) {
        return (React.createElement(Button, { inverted: true, size: "small", fullWidth: fullWidth, href: buyAgain.link }, buyAgain.buttonText));
    }
    // Set on digital file lines, where the button opens the subject's pictures.
    const subjectUuid = "subjects" in orderLine ? (_b = (_a = orderLine.subjects) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.uuid : undefined;
    if (subjectUuid) {
        return (React.createElement(Button, { inverted: true, size: "small", fullWidth: fullWidth, component: Link, to: generatePath(routes["myaccount.picturesets.pictureset"].path, {
                uuid: subjectUuid,
            }) }, buyAgain.buttonText));
    }
    const handleClick = () => __awaiter(void 0, void 0, void 0, function* () {
        setInProgress(true);
        try {
            yield onBuyAgain(orderLine);
        }
        finally {
            setInProgress(false);
        }
    });
    return (React.createElement(Button, { inverted: true, size: "small", fullWidth: fullWidth, disabled: inProgress, onClick: handleClick }, buyAgain.buttonText));
};
const PackageContents = ({ orderLine, variant, }) => {
    const products = groupPackageItemsByProduct(orderLine.package.packageItems);
    return (React.createElement(PackageProductList, null, products.map((product) => (React.createElement("li", { key: product.uuid },
        React.createElement(Typography, { variant: variant, fontWeight: "bold", component: "div" }, product.name),
        React.createElement("ul", null, product.packageItems.map((packageItem) => (React.createElement("li", { key: packageItem.uuid },
            React.createElement(Typography, { variant: variant }, getPackageItemDescription(packageItem)))))))))));
};
// Digital file and digital set lines: which subjects the pictures are of, as on
// the desktop order history.
const SubjectList = ({ subjects, variant, }) => (React.createElement(PackageProductList, null, subjects.map((subject) => (React.createElement("li", { key: subject.uuid },
    React.createElement(Typography, { variant: variant, fontWeight: "bold", component: "div" },
        subject.name,
        subject.numImages ? ` (${subject.numImages})` : null),
    React.createElement(Typography, { variant: variant, component: "div" },
        subject.group,
        " ",
        subject.job.name))))));
export const OrderHistoryLine = ({ orderLine, onBuyAgain, }) => {
    var _a, _b;
    const { t } = useTranslation();
    const isMobile = useIsMobileDevice();
    const descriptionVariant = isMobile ? "caption" : "body2";
    return (React.createElement(Line, null,
        React.createElement(PreviewColumn, null,
            React.createElement(ProductPreview, { orderLine: orderLine })),
        React.createElement(DetailsColumn, null,
            React.createElement(Typography, { color: "primary", fontWeight: 500 }, getProductTitle(orderLine, t)),
            React.createElement(Box, { marginBottom: 1 }, "package" in orderLine ? (React.createElement(PackageContents, { orderLine: orderLine, variant: descriptionVariant })) : "subjects" in orderLine && ((_a = orderLine.subjects) === null || _a === void 0 ? void 0 : _a.length) ? (React.createElement(SubjectList, { subjects: orderLine.subjects, variant: descriptionVariant })) : (React.createElement(Typography, { variant: "body2", fontStyle: "italic", component: "div" },
                React.createElement(SubProducts, { productUuid: orderLine.product.uuid, subProductUuids: (_b = orderLine.subProductUuids) !== null && _b !== void 0 ? _b : [] }),
                React.createElement(OrderLineShortDescription, { orderLine: orderLine })))),
            React.createElement(PriceRow, null,
                React.createElement(Typography, { variant: "body2" }, t("%s each", {
                    postProcess: "sprintf",
                    sprintf: [
                        formatCurrency(orderLine.linePrice / orderLine.quantity),
                    ],
                })),
                React.createElement(Box, { textAlign: "center" },
                    React.createElement(Typography, { variant: "caption", component: "div" }, t("Quantity")),
                    React.createElement(QuantityBox, null,
                        React.createElement(Typography, { variant: "body2" }, orderLine.quantity)))),
            React.createElement(Box, { marginTop: 1.5 },
                orderLine.rebate && (React.createElement(React.Fragment, null,
                    React.createElement(PriceRow, null,
                        React.createElement(Typography, { variant: "body2" }, t("Original price")),
                        React.createElement(Typography, { variant: "body2", sx: { textDecoration: "line-through" }, color: "text.secondary" }, formatCurrency(orderLine.linePrice))),
                    React.createElement(PriceRow, null,
                        React.createElement(Typography, { variant: "body2" }, t("Rebate")),
                        React.createElement(Typography, { variant: "body2" }, formatCurrency(orderLine.rebate.linePrice))))),
                React.createElement(PriceRow, null,
                    React.createElement(Typography, { variant: "body2", fontWeight: "bold" }, t("Subtotal")),
                    React.createElement(Typography, { variant: "body2", fontWeight: "bold" }, formatCurrency(orderLine.actualLinePrice)))),
            orderLine.buyAgain && (React.createElement(Box, { marginTop: 1.5 },
                React.createElement(BuyAgainButton, { orderLine: orderLine, buyAgain: orderLine.buyAgain, onBuyAgain: onBuyAgain }))))));
};
